// lib/services/compute/sticker_cpu_slot.ts

import BaseSlot, { SlotState } from "../lib/services/compute/base_slot";
import { rb_error, rb_log, rb_map_state, rb_throttle } from "../lib/services/compute/rb_log";

/**
 * Outcome of begin_map(). Distinguishes three cases so the producer can
 * take the correct pool action without a second error being logged:
 *  - ok        : mapped successfully, slot is Ready and publishable.
 *  - cancelled : the map was aborted because a clear()/recycle requested
 *                cancellation of THIS operation, or a newer generation took
 *                the slot. Not a real failure — no rb_error was emitted.
 *  - (ok=false, cancelled=false) : a genuine map failure (error was logged).
 * `token` is the slot generation this begin_map operated on, so the pool can
 * refuse to recycle a slot that a newer operation now owns.
 */
export interface BeginMapResult {
  ok: boolean;
  cancelled: boolean;
  token: number;
}

/**
 * Per-frame readback slot for sticker detection results.
 *
 * Holds a mappable GPU buffer that receives a copy of the sticker buffer
 * from a ColorGpuSlot, plus a CPU-side typed array view of the mapped
 * data.
 *
 * Lifecycle:
 *   Idle -> Recording (copy command encoded) -> Pending (mapAsync in
 *   flight) -> Ready (data available on CPU) -> Leased (consumer
 *   reading) -> Idle.
 */
export default class StickerCpuSlot extends BaseSlot {
  sticker_readback_buffer: GPUBuffer | null = null;
  blob_readback_buffer: GPUBuffer | null = null;

  sticker_buffer_size: number = 0;
  blob_buffer_size: number = 0;

  mapped_sticker_data: Uint8Array | null = null;
  mapped_blob_data: Uint8Array | null = null;


  // Which gpu slot sequence this readback corresponds to.
  source_gpu_sequence: number = 0;

  // Identity for logging only.
  slot_index: number = -1;
  slot_key: string = "";

  // ---- Race-safe cancellation state -------------------------------------
  // Monotonic operation token. Bumped whenever the slot starts a new fill
  // (begin_record) or is recycled. begin_map() captures this at entry and,
  // after every await, aborts without disturbing the slot if it no longer
  // matches — that means a newer operation now owns the slot.
  private generation: number = 0;

  // Deferred-cancellation request. Set by request_clear()/request_cancel()
  // when the slot has an in-flight/pending map (state === Pending). The
  // begin_map() continuation performs the actual unmap + state reset when it
  // next observes this, so we never unmap a buffer out from under an awaiting
  // mapAsync (the OperationError "not mapped or destroyed" root cause).
  private cancel_requested: boolean = false;

  constructor() {
    super(false);
  }

  /** Current operation token (identity of the in-progress map, if any). */
  get current_generation(): number {
    return this.generation;
  }

  /** True if `token` still identifies the operation that owns this slot. */
  owns_generation(token: number): boolean {
    return this.generation === token;
  }

  allocate_buffer(device: GPUDevice, key: string, slot_index: number, sticker_size: number,blob_size: number): void {
    this.sticker_buffer_size = sticker_size;
    this.blob_buffer_size = blob_size;
    this.slot_index = slot_index;
    this.slot_key = key;

    this.sticker_readback_buffer = device.createBuffer({
    label: `${key}_sticker_readback_${slot_index}`,
    size: sticker_size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });

  this.blob_readback_buffer = device.createBuffer({
    label: `${key}_blob_readback_${slot_index}`,
    size: blob_size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
    
  }

  begin_record(): boolean {
    if (this.state !== SlotState.Idle) return false;
    if (this.mapped_sticker_data) return false;
    if (this.mapped_blob_data) return false;

    if (this.sticker_readback_buffer?.mapState &&
    this.sticker_readback_buffer.mapState !== "unmapped") return false;

    if (this.blob_readback_buffer?.mapState &&
      this.blob_readback_buffer.mapState !== "unmapped") return false;

    // A new operation begins: advance the token and clear any stale
    // cancellation request from a previous op on this slot.
    this.generation += 1;
    this.cancel_requested = false;

    this.transition_to(SlotState.Recording);
    return true;
  }

  /**
   * Encode the copy from the gpu slot's sticker buffer into this
   * readback buffer. Called during command encoding, before submit.
   */
  encode_copy(
    encoder: GPUCommandEncoder,
    sticker_source_buffer: GPUBuffer,
    blob_source_buffer: GPUBuffer,
    gpu_sequence: number
  ): void {

     /* console.log("[encode_copy]", {
      gpu_sequence,
      sticker_source_label: sticker_source_buffer.label,
      blob_source_label: blob_source_buffer.label,
      sticker_readback_label: this.sticker_readback_buffer?.label,
      blob_readback_label: this.blob_readback_buffer?.label,
      blob_buffer_size: this.blob_buffer_size,
      });*/

      encoder.copyBufferToBuffer(
      sticker_source_buffer, 0,
      this.sticker_readback_buffer!, 0,
      this.sticker_buffer_size
    );

    encoder.copyBufferToBuffer(
      blob_source_buffer, 0,
      this.blob_readback_buffer!, 0,
      this.blob_buffer_size
    );
    this.source_gpu_sequence = gpu_sequence;
    this.transition_to(SlotState.Pending);
  }

  /**
   * Start the async map operation. Called after the command buffer has
   * been submitted. Resolves when the data is available on CPU.
   */
  /**
   * True if the current begin_map operation must abort: either a
   * cancellation was requested for this op, or a newer generation has taken
   * over the slot. Checked after every await and before every
   * getMappedRange / transition / publish.
   */
  private should_abort_map(token: number): boolean {
    return this.cancel_requested || this.generation !== token;
  }

  /**
   * Cleanly finish a cancelled/stale begin_map WITHOUT logging an error.
   * If this op still owns the slot (generation matches), it safely unmaps
   * anything it mapped, drops the CPU views, and returns the slot to Idle.
   * If a newer generation owns the slot, everything is left untouched so we
   * never disturb the newer operation's buffers or state.
   */
  private finish_cancelled_map(token: number, phase: string): BeginMapResult {
    const stale = this.generation !== token;

    if (!stale) {
      if (this.sticker_readback_buffer?.mapState === "mapped") {
        try { this.sticker_readback_buffer.unmap(); } catch {}
      }
      if (this.blob_readback_buffer?.mapState === "mapped") {
        try { this.blob_readback_buffer.unmap(); } catch {}
      }
      this.mapped_sticker_data = null;
      this.mapped_blob_data = null;
      if (this.state === SlotState.Pending) {
        this.transition_to(SlotState.Idle);
      }
      this.cancel_requested = false;
    }

    // Info-level only (gated by RB_INFO_ENABLED): never rb_error/console.error.
    if (rb_throttle("begin_map.cancelled")) {
      rb_log("begin_map.cancelled", {
        slot: this.slot_index,
        key: this.slot_key,
        seq: this.source_gpu_sequence,
        phase,
        stale,
        token,
        generation: this.generation,
      });
    }

    return { ok: false, cancelled: true, token };
  }

  async begin_map(): Promise<BeginMapResult> {
  const token = this.generation;
  if (this.state !== SlotState.Pending) {
    return { ok: false, cancelled: false, token };
  }

  // Cancellation may already have been requested before the map started
  // (e.g. clear() fired between encode_copy and this continuation running).
  if (this.should_abort_map(token)) {
    return this.finish_cancelled_map(token, "pre_map");
  }

  // Logging only: track which sub-step is in flight so a failure pinpoints
  // sticker vs blob and mapAsync vs getMappedRange.
  let phase: "sticker_map" | "sticker_range" | "blob_map" | "blob_range" =
    "sticker_map";
  const t_start = performance.now();

  // Throttled baseline snapshot on entry. Failures are logged unthrottled
  // below with the same (plus error) fields, so this only avoids per-frame spam.
  if (rb_throttle("begin_map.enter")) {
    rb_log("begin_map.enter", {
      slot: this.slot_index,
      key: this.slot_key,
      seq: this.source_gpu_sequence,
      state: SlotState[this.state],
      sticker_mapState: rb_map_state(this.sticker_readback_buffer),
      blob_mapState: rb_map_state(this.blob_readback_buffer),
      sticker_size: this.sticker_buffer_size,
      blob_size: this.blob_buffer_size,
    });
  }

  try {
    phase = "sticker_map";
    await this.sticker_readback_buffer!.mapAsync(
      GPUMapMode.READ,
      0,
      this.sticker_buffer_size
    );
    if (this.should_abort_map(token)) return this.finish_cancelled_map(token, phase);

    phase = "sticker_range";
    const sticker_range = this.sticker_readback_buffer!.getMappedRange(
      0,
      this.sticker_buffer_size
    );
    this.mapped_sticker_data = new Uint8Array(sticker_range);
    if (this.should_abort_map(token)) return this.finish_cancelled_map(token, phase);

    phase = "blob_map";
    await this.blob_readback_buffer!.mapAsync(
      GPUMapMode.READ,
      0,
      this.blob_buffer_size
    );
    if (this.should_abort_map(token)) return this.finish_cancelled_map(token, phase);

    phase = "blob_range";
    const blob_range = this.blob_readback_buffer!.getMappedRange(
      0,
      this.blob_buffer_size
    );
    this.mapped_blob_data = new Uint8Array(blob_range);
    if (this.should_abort_map(token)) return this.finish_cancelled_map(token, phase);

    this.transition_to(SlotState.Ready);

    if (rb_throttle("begin_map.ok")) {
      rb_log("begin_map.ok", {
        slot: this.slot_index,
        seq: this.source_gpu_sequence,
        elapsed_ms: Math.round((performance.now() - t_start) * 100) / 100,
      });
    }
    return { ok: true, cancelled: false, token };
  } catch (err) {
    // If the exception is a side-effect of a requested cancellation or a
    // stale generation, treat it as a clean cancellation (no error log).
    if (this.should_abort_map(token)) {
      return this.finish_cancelled_map(token, phase);
    }

    const e = err as { name?: string; message?: string };
    rb_error("begin_map.failed", {
      phase,
      slot: this.slot_index,
      key: this.slot_key,
      seq: this.source_gpu_sequence,
      state: SlotState[this.state],
      sticker_mapState: rb_map_state(this.sticker_readback_buffer),
      blob_mapState: rb_map_state(this.blob_readback_buffer),
      sticker_size: this.sticker_buffer_size,
      blob_size: this.blob_buffer_size,
      elapsed_ms: Math.round((performance.now() - t_start) * 100) / 100,
      error_name: e?.name,
      error_message: e?.message,
    });
    console.error("StickerCpuSlot.begin_map failed", err);

    try { this.sticker_readback_buffer?.unmap(); } catch {}
    try { this.blob_readback_buffer?.unmap(); } catch {}

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;
    this.transition_to(SlotState.Idle);
    return { ok: false, cancelled: false, token };
  }
}

  /**
   * Logging only: warn when an unconditional unmap is about to abort an
   * in-flight (pending) mapAsync. Emits only when a buffer is `pending`.
   */
  private log_if_pending_unmap(action: string): void {
    const sticker_mapState = rb_map_state(this.sticker_readback_buffer);
    const blob_mapState = rb_map_state(this.blob_readback_buffer);
    if (sticker_mapState === "pending" || blob_mapState === "pending") {
      rb_error("unmap_while_pending", {
        action,
        slot: this.slot_index,
        key: this.slot_key,
        state: SlotState[this.state],
        seq: this.source_gpu_sequence,
        sticker_mapState,
        blob_mapState,
      });
    }
  }

  /**
   * Pool-level clear request. Returns true if cleanup was DEFERRED because a
   * map operation is in flight/pending for this slot (state === Pending); in
   * that case we only set the cancellation flag and leave the slot untouched
   * (still Pending, so it is NOT acquirable) — the begin_map() continuation
   * safely unmaps and returns it to Idle when it next checks. Returns false
   * if the slot was safe and has been cleared immediately; the caller then
   * marks it Idle/never-used.
   *
   * This is the fix for the clear-vs-map race: a slot with a pending mapAsync
   * is never unmapped out from under the awaiting continuation.
   */
  request_clear(): boolean {
    if (this.state === SlotState.Pending) {
      this.cancel_requested = true;
      return true; // deferred; begin_map() will finalize this slot
    }

    // Safe: no begin_map() continuation owns this slot's buffers.
    try { this.sticker_readback_buffer?.unmap(); } catch {}
    try { this.blob_readback_buffer?.unmap(); } catch {}

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;
    return false;
  }
  
  unmap_and_recycle(): void {
  if (this.sticker_readback_buffer?.mapState === "mapped") {
    this.sticker_readback_buffer.unmap();
  }
  if (this.blob_readback_buffer?.mapState === "mapped") {
    this.blob_readback_buffer.unmap();
  }

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  this.recycle();
}

cancel_and_recycle(): void {
  this.log_if_pending_unmap("cancel_and_recycle");
  try { this.sticker_readback_buffer?.unmap(); } catch {}
  try { this.blob_readback_buffer?.unmap(); } catch {}

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  this.recycle();
}

recycle(): void {
  if (this.sticker_readback_buffer && this.mapped_sticker_data) {
    try { this.sticker_readback_buffer.unmap(); } catch {}
  }
  if (this.blob_readback_buffer && this.mapped_blob_data) {
    try { this.blob_readback_buffer.unmap(); } catch {}
  }

  this.mapped_sticker_data = null;
  this.mapped_blob_data = null;
  this.source_gpu_sequence = 0;
  // Advance the token so any begin_map() continuation still holding an older
  // token treats this slot as stale and refuses to touch it.
  this.generation += 1;
  this.cancel_requested = false;
  super.recycle();
}

  destroy_buffers(): void {
    this.log_if_pending_unmap("destroy_buffers");
    if (this.sticker_readback_buffer) {
      try { this.sticker_readback_buffer.unmap(); } catch {}
      this.sticker_readback_buffer.destroy();
      this.sticker_readback_buffer = null;
    }

    if (this.blob_readback_buffer) {
      try { this.blob_readback_buffer.unmap(); } catch {}
      this.blob_readback_buffer.destroy();
      this.blob_readback_buffer = null;
    }

    this.mapped_sticker_data = null;
    this.mapped_blob_data = null;
  }
    
}